import { FeatureCollection } from "geojson";
import { toApiError } from "@services/api";
import type { TPrescriptionLayerSpec, TPrescriptionSurface } from "./fieldsService";

const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;

/** Multi-layer mode: blend specific yield seasons / the productivity map. */
export type PrescriptionLayersOption = {
  layers: TPrescriptionLayerSpec[];
  surface: TPrescriptionSurface;
};

/**
 * Fetch prescription zone data from the backend.
 *
 * Single-layer: GET /api/v1/fields/:id/prescription?seasonId=...&zones=...
 * Multi-layer:  GET /api/v1/fields/:id/prescription?layers=<json>&surface=...&zones=...
 *
 * Returns a GeoJSON FeatureCollection where each Feature has
 * { zone: number, fill: string } in its properties.
 *
 * The optional `interpolate` callback is kept for API compatibility with
 * the PrescriptionScreen but is no longer used — the backend assigns colours.
 */
export const fetchPrescription = async (
  field_id: string,
  count: number,
  interpolate?: (n: number, max: number) => string,
  seasonId?: string,
  combined?: PrescriptionLayersOption,
): Promise<FeatureCollection> => {
  const q = new URLSearchParams();
  q.append("zones", count.toString());

  if (combined && combined.layers.length > 0) {
    q.append("layers", JSON.stringify(combined.layers));
    q.append("surface", combined.surface);
  } else if (seasonId) {
    q.append("seasonId", seasonId);
  } else {
    console.warn("fetchPrescription: no seasonId or layers provided, returning empty");
    return { type: "FeatureCollection", features: [] };
  }

  const url = `${BASE_URL}/api/v1/fields/${field_id}/prescription?${q.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    // A 409 PRESCRIPTION_IN_PROGRESS means the user already has a prescription
    // generating (backend "one at a time" guard); toApiError surfaces it as
    // `error.code` so the screen shows its localized "please wait" panel. (This
    // used to read `body.message`, which the backend never sends, so the panel
    // never appeared.)
    throw await toApiError(response, `Prescription API request failed: ${response.status}`);
  }

  const data: FeatureCollection = await response.json();
  return data;
};
